/**
 * Module-level singleton cache provider for SWR.
 *
 * This cache is initialized once when the module loads and persists for the entire
 * application lifetime. It:
 * - Restores previously saved public data from localStorage on first import
 * - Saves public data to localStorage when the page is hidden or unloaded
 * - Returns the same Map instance on every call to ensure stability
 *
 * CRITICAL: This must be a singleton to prevent cache resets on re-renders.
 *
 * Only keys under PERSISTED_KEY_PREFIX are written to localStorage, and only their `data`. Authenticated
 * data (customer info, addresses, payment methods, orders) lives in memory alone, so it cannot outlive
 * the session or be read from storage by the next person on the device. In-flight flags and errors are
 * not saved either, so a page unloaded mid-fetch cannot come back stuck in a loading state.
 */
const STORAGE_KEY = "app-cache";
const PERSISTED_KEY_PREFIX = "public/";
/**
 * Identifies the build that wrote the saved cache. The bundle is loaded as `bundle.js?version=<BuildVer>`
 * (or `bundle.js?version=13&<BuildVer>` on the order flow page), so the last token of the query is the
 * build. Entries saved by another build are discarded instead of rendering old response shapes.
 * Returns null when it cannot be read, which turns persistence off.
 */
const getBuildStamp = () => {
    var _a;
    try {
        const bundleSrc = Array.from(document.scripts)
            .map((script) => script.src)
            .find((src) => /bundle\.js\?/.test(src));
        if (!bundleSrc) {
            return null;
        }
        return (_a = new URL(bundleSrc).search.split(/[?&=]/).filter(Boolean).pop()) !== null && _a !== void 0 ? _a : null;
    }
    catch (_b) {
        return null;
    }
};
const removeStoredCache = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
    }
    catch (_a) {
        // Storage is unavailable; nothing to remove.
    }
};
const isPersistedKey = (key) => typeof key === "string" && key.startsWith(PERSISTED_KEY_PREFIX);
const readStoredEntries = (buildStamp) => {
    if (!buildStamp) {
        return [];
    }
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            return [];
        }
        const parsed = JSON.parse(stored);
        // Anything not written by this build in this format (including the old bare-array format) is dropped.
        if ((parsed === null || parsed === void 0 ? void 0 : parsed.version) !== buildStamp || !Array.isArray(parsed.entries)) {
            removeStoredCache();
            return [];
        }
        return parsed.entries.filter((entry) => Array.isArray(entry) && entry.length === 2 && isPersistedKey(entry[0]));
    }
    catch (_a) {
        // Storage blocked or the stored value is corrupt: start with an empty cache.
        removeStoredCache();
        return [];
    }
};
const buildStamp = getBuildStamp();
// Initialize the cache Map once at module level
const cacheMap = new Map(readStoredEntries(buildStamp));
const persistCache = () => {
    if (!buildStamp) {
        return;
    }
    try {
        const entries = Array.from(cacheMap.entries())
            .filter(([key, state]) => isPersistedKey(key) && (state === null || state === void 0 ? void 0 : state.data) !== undefined)
            .map(([key, state]) => [key, { data: state.data }]);
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: buildStamp, entries }));
    }
    catch (_a) {
        // Quota exceeded or storage blocked: drop the stale copy rather than leave a partial one.
        removeStoredCache();
    }
};
// `pagehide` is delivered on tab close, navigation and bfcache; `beforeunload` is not reliable on mobile.
window.addEventListener("pagehide", persistCache);
document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
        persistCache();
    }
});
// Provider function always returns the same singleton Map
export const localStorageProvider = () => {
    return cacheMap;
};
